import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import useIfmsColSizes from '../../hooks/useIfmsColSizes';

import {
  infoAreaBicHistoryTable,
  infoAreaBicHistoryLineItemsTable,
  infoAreaBicRevisionHistoryLineItemsTable,
  ApplicableTableSettings,
} from '../../tableSettings';

import {
  TextRenderer,
  TextAlignLeftRenderer,
  DateTimeRenderer,
  DateRenderer,
} from '../../uiControls/SmartTableRenderers';

import Label from '../../ui/Label';
import LabeledTextBox from '../../ui/LabeledTextBox';
import LabeledControl from '../../ui/LabeledControl';

import SmartTable from '../../uiControls/SmartTable';
import CurrencyLabel from '../../uiControls/CurrencyLabel';
import SalesCostTypeLabel from '../../uiControls/SalesCostTypeLabel';
import useTableRowSelection from '../../ui/tables/useTableRowSelection';
import Currency from '../../uiControls/Currency';
import { AcctHistHd } from '../../fms.core/AcctHistHd';
import _ from 'lodash';

function getDetailLabel(context:string) {
  switch (context) {
    case 'bill':
      return 'Bill Detail';
    case 'invoice':
      return 'Invoice Detail';
    case 'crdb':
      return 'CrDb Detail';
    default:
      throw new Error('Unsupported context');
  }
}

function getAmountLabel(context:string) {
  switch (context) {
    case 'bill':
      return 'Bill Amount';
    case 'invoice':
      return 'Invoice Amount';
    case 'crdb':
      return 'CrDb Amount';
    default:
      throw new Error('Unsupported context');
  }
}

function getBicNoLabel(context:string) {
  switch (context) {
    case 'bill':
      return 'Bill #';
    case 'invoice':
      return 'Invoice #';
    case 'crdb':
      return 'CrDb #';
    default:
      throw new Error('Unsupported context');
  }
}

function getBicDateLabel(context:string) {
  switch (context) {
    case 'bill':
      return 'Bill Date';
    case 'invoice':
      return 'Invoice Date';
    case 'crdb':
      return 'CrDb Date';
    default:
      throw new Error('Unsupported context');
  }
}

function getBicCompanyLabel(context:string) {
  switch (context) {
    case 'bill':
      return 'Vendor (Cost)';
    case 'invoice':
      return 'Invoice To';
    case 'crdb':
      return 'Agent';
    default:
      throw new Error('Unsupported context');
  }
}

function getRevisionHistoryLabel(context:string) {
  switch (context) {
    case 'bill':
      return 'Bill Revision History';
    case 'invoice':
      return 'Invoice Revision History';
    case 'crdb':
      return 'CrDb Revision History';
    default:
      throw Error('Unsupported context');
  }
}


function CurrencyRenderer(value:any, currency:any, isLast:any) {
  return (
    <div style={{marginTop: '0px', paddingRight: !isLast ? '15px' : '25px', textAlign: 'right'}}>
      <CurrencyLabel currency={currency} value={value} style={{padding: '0', marign: '0', lineHeight: '12px'}} />
    </div>
  );
}

function SalesCostTypeLabelRenderer({cell}:any) {
  return (
    <div style={{marginTop: '0px', marginLeft: '5px', textAlign: 'left'}}>
      <SalesCostTypeLabel value={cell.value} />
    </div>
  );
}

/**
 * 
 * @param param0 historyData: AcctHistHd
 * @returns 
 */
function BicInfoAreaHistory({historyData, primaryCurrency, context}: { historyData: AcctHistHd, primaryCurrency: string, context: string}) {
  const { row6 } = useIfmsColSizes();
  const { selectTableRow } = useTableRowSelection('ifms-app-info-area', 'info-area-bic-history-table', null, onHistoryClick);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
    selectTableRow(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyData]);

  function onHistoryClick(index:number) {
    setSelectedIndex(index);
  }

  const history = (historyData as AcctHistHd)?.historyHDList || [];
  const revisionHistory = (historyData as AcctHistHd)?.revisionHistList || [];

  const historyTableOptions:ApplicableTableSettings = {
    customTableId: 'info-area-bic-history-table',
    tableName: 'infoAreaBicHistoryTable',
    ...infoAreaBicHistoryTable,
    data: history,
    selectable: true,
    
    width: `${row6}px`,
    rowRenderers: [
      { accessor: 'ediDate', Cell: DateTimeRenderer },
      { accessor: 'ediId', Cell: TextAlignLeftRenderer },
      { accessor: 'refNo', Cell: TextRenderer },
      { accessor: 'currency', Cell: TextRenderer },
      // @ts-ignore:next-line
      { accessor: 'amount', Cell: ({cell}) => CurrencyRenderer(cell.row.values.amount, cell.row.values.currency) },
      { accessor: 'invoNo', Header: () => <span>{getBicNoLabel(context)}</span>, Cell: TextAlignLeftRenderer },
      { accessor: 'invoDate', Header: () => <span>{getBicDateLabel(context)}</span>, Cell: DateRenderer },
      { accessor: 'dueDate', Cell: DateRenderer },
      { accessor: 'custName', Header: () => <span>{getBicCompanyLabel(context)}</span>, Cell: TextAlignLeftRenderer },
    ],
  };

  const lineItemsData = history.length > 0 && selectedIndex >= 0 && selectedIndex < history.length
    ? (historyData as AcctHistHd).historyDetaillAllList.filter(x => x.ediHdId === history[selectedIndex].id)        //history[selectedIndex]lineItems.map((l:any, i:number) => ({...l, no: i + 1}))
    : [];

  const historyLineItemsTableOptions:ApplicableTableSettings = {
    tableName: 'infoAreaBicHistoryLineItemsTable',
    ...infoAreaBicHistoryLineItemsTable,
    data: lineItemsData,
    selectable: true,
    
    width: `${row6}px`,
    rowRenderers: [
      { accessor: 'seqNo', Cell: TextRenderer },
      { accessor: 'chgTypeDesc', Cell: SalesCostTypeLabelRenderer },
      { accessor: 'code', Cell: TextAlignLeftRenderer },
      { accessor: 'description', Cell: TextAlignLeftRenderer },
      { accessor: 'currency', Cell: TextRenderer },
      {
        accessor: 'amount',
        Header: () => <div style={{marginRight: '8px'}}>{primaryCurrency} Amount</div>,
        Cell: ({cell}:any) => CurrencyRenderer(cell.row.values.amount, cell.row.values.currency, true)
      },
    ],
  };

  const revisionHistoryLineItemsTableOptions:ApplicableTableSettings = {
    tableName: 'infoAreaBicRevisionHistoryLineItemsTable',
    ...infoAreaBicRevisionHistoryLineItemsTable,
    data: revisionHistory.map((x:any) => ({...x, action: 'PRINT'})),
    selectable: true,
    
    rowRenderers: [
      { accessor: 'action', Cell: TextRenderer },
      { accessor: 'u2Date', Cell: TextRenderer },
      { accessor: 'invoNo', Header: () => <span>{getBicNoLabel(context)}</span>, Cell: TextAlignLeftRenderer },
    ],
  };

  const labelSize = '110px';
  const sectionSize = '190px';

  return (
    <div>
      <div className="ifms-container">
        <div className="ifms-row6">
          <div className="float-left" style={{width: sectionSize}}>
            <Label label={getDetailLabel(context)} bold/>
          </div>
          <div className="float-left" style={{width: sectionSize}}>
            <LabeledControl
              labelSize={labelSize}
              alignRight
              label={getAmountLabel(context)}
              control={<Currency disabled dark value={historyData?.amount} currency={_.last(historyData.historyHDList)?.currency ?? ''} />}
            />
          </div>
          <div className="float-left" style={{width: sectionSize}}>
            <LabeledTextBox labelSize={labelSize} alignRight label="Recognize date" value={historyData.confirmDate} disabled dark />
          </div>
          <div className="float-left" style={{width: sectionSize}}>
            <LabeledTextBox labelSize={labelSize} alignRight label="R. Action Date" value={historyData.confirmActionDate} disabled dark />
          </div>

          <div className="clearfix"></div>

          <SmartTable {...historyTableOptions} />

          <br />
          <Label label="Billing Detail" bold/>
          <div className="clearfix"></div>

          <SmartTable {...historyLineItemsTableOptions} />
        </div>

        <div className="ifms-row6">
          <Label label={getRevisionHistoryLabel(context)} bold/>
          <div className="clearfix"></div>

          <div style={{float: 'left', width: '300px'}}>
            <SmartTable {...revisionHistoryLineItemsTableOptions} />
          </div>
        </div>
      </div>
    </div>
  );
}

BicInfoAreaHistory.defaultProps = {
};

BicInfoAreaHistory.propTypes = {
  primaryCurrency: PropTypes.string.isRequired,
  historyData: PropTypes.object,
  context: PropTypes.oneOf(['invoice', 'bill', 'crdb']).isRequired,
};


export default React.memo(BicInfoAreaHistory);
